import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    IconButton,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    useTheme,
    Menu,
    TablePagination,
    Avatar,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert,
    Snackbar,
    FormControl,
    InputLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import RefreshIcon from '@mui/icons-material/Refresh';
import { tppService, ThirdPartyProcessor, ProcessorType } from '../../services/tpp-service';
import { useNavigate } from 'react-router-dom';
import AddProcessorPanel from './AddProcessorPanel';
import EditProcessorPanel from './EditProcessorPanel';
import { formatFriendlyDate } from '../../utils/dateUtils';

const PROCESSOR_TYPES: ProcessorType[] = ['PROCESSOR', 'FIDUCIARY'];

const Processors: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('newest');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [processors, setProcessors] = useState<ThirdPartyProcessor[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
    const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
    const [selectedProcessor, setSelectedProcessor] = useState<ThirdPartyProcessor | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [filters, setFilters] = useState({
        type: 'ALL',
        isPublished: 'ALL'
    });
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        loadProcessors();
    }, [page, rowsPerPage, sortBy]);

    const loadProcessors = async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await tppService.getProcessors({
                filters: {
                    type: filters.type === 'ALL' ? undefined : filters.type as ProcessorType,
                    isPublished: filters.isPublished === 'ALL' ? undefined : filters.isPublished === 'PUBLISHED'
                },
                sortBy: sortBy.toLowerCase() as 'a-z' | 'z-a' | 'newest' | 'oldest',
                pagination: {
                    page: page + 1,
                    pageSize: rowsPerPage
                }
            });
            setProcessors(result.processors);
            setTotalCount(result.total);
        } catch (error) {
            console.error('Error loading processors:', error);
            setError(error instanceof Error ? error.message : 'Failed to load processors');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    

    const handleFilterChange = (field: keyof typeof filters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApplyFilters = async () => {
        await loadProcessors();
        setIsFilterModalOpen(false);
    };

    const hasActiveFilters = () => {
        return filters.type !== 'ALL' || filters.isPublished !== 'ALL';
    };

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                color: theme.palette.text.primary
            }}>
                Loading processors...
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, bgcolor: theme.palette.background.default }}>
            <Typography variant="h4" sx={{ 
                mb: theme.custom.titleSpacing.marginBottom, 
                color: theme.palette.text.primary 
            }}>
                Processors
            </Typography>

            {error && (
                <Snackbar 
                    open={!!error} 
                    autoHideDuration={6000} 
                    onClose={() => setError(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                        {error}
                    </Alert>
                </Snackbar>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 'auto' }}>
                    <Tooltip title="Refresh">
                        <IconButton 
                            onClick={loadProcessors}
                            sx={{ 
                                color: theme.palette.text.primary,
                                '&:hover': {
                                    bgcolor: theme.palette.action.hover,
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        size="small"
                        sx={{ 
                            width: 150,
                            bgcolor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.divider,
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                            },
                            '& .MuiSelect-icon': {
                                color: theme.palette.text.secondary,
                            },
                        }}
                    >
                        <MenuItem value="A-Z" sx={{ color: theme.palette.text.primary }}>A-Z</MenuItem>
                        <MenuItem value="Z-A" sx={{ color: theme.palette.text.primary }}>Z-A</MenuItem>
                        <MenuItem value="newest" sx={{ color: theme.palette.text.primary }}>Newest</MenuItem>
                        <MenuItem value="oldest" sx={{ color: theme.palette.text.primary }}>Oldest</MenuItem>
                    </Select>
                    <IconButton 
                        onClick={() => setIsFilterModalOpen(true)}
                        sx={{ 
                            color: theme.palette.text.primary,
                            '&:hover': {
                                bgcolor: theme.palette.action.hover,
                            },
                            position: 'relative',
                            '&::after': hasActiveFilters() ? {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: theme.palette.primary.main,
                                border: `2px solid ${theme.palette.background.paper}`,
                            } : {}
                        }}
                    >
                        <FilterListIcon />
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setIsAddPanelOpen(true)}
                        sx={{ 
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '&:hover': { 
                                bgcolor: theme.palette.primary.dark 
                            },
                            '& .MuiSvgIcon-root': {
                                color: theme.palette.primary.contrastText,
                            }
                        }}
                    >
                        ADD NEW
                    </Button>
                </Box>
            </Box>

            <TableContainer 
                component={Paper}
                sx={{
                    bgcolor: theme.palette.background.paper,
                    '& .MuiTable-root': {
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                    }
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ color: theme.palette.text.primary }}>Name</TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>Type</TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>Contact Email</TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>Contact Phone</TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>Created on</TableCell>
                            <TableCell sx={{ color: theme.palette.text.primary }}>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {processors.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} sx={{ borderBottom: 'none' }}>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        py: 8
                                    }}>
                                        <Typography 
                                            variant="h6" 
                                            sx={{ 
                                                mb: 3,
                                                color: theme.palette.text.secondary,
                                                textAlign: 'center'
                                            }}
                                        >
                                            There are no processors to display. Click the button below to create your first processor
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => setIsAddPanelOpen(true)}
                                            sx={{ 
                                                bgcolor: theme.palette.primary.main,
                                                color: theme.palette.primary.contrastText,
                                                '&:hover': { 
                                                    bgcolor: theme.palette.primary.dark 
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    color: theme.palette.primary.contrastText,
                                                }
                                            }}
                                        >
                                            ADD NEW
                                        </Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            processors.map((processor) => (
                                <TableRow
                                    key={processor.processorId}
                                    hover
                                    sx={{
                                        '&.MuiTableRow-root:hover': {
                                            bgcolor: theme.palette.action.hover,
                                        }
                                    }}
                                >
                                    <TableCell 
                                        sx={{ 
                                            color: theme.palette.text.primary,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                color: theme.palette.primary.main,
                                                textDecoration: 'underline'
                                            }
                                        }}
                                        onClick={() => {
                                            setSelectedProcessor(processor);
                                            setIsEditPanelOpen(true);
                                        }}
                                    >
                                        {processor.name}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={processor.type}
                                            color={processor.type === 'FIDUCIARY' ? 'primary' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={processor.contactEmail}>
                                            <Typography 
                                                sx={{ 
                                                    maxWidth: '200px', 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap' 
                                                }}
                                            >
                                                {processor.contactEmail}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{processor.contactPhone}</TableCell>
                                    <TableCell>
                                        {processor.createdDate ? 
                                            formatFriendlyDate(new Date(processor.createdDate).toISOString()) : 
                                            '-'
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={processor.isPublished ? 'Published' : 'Draft'}
                                            size="small"
                                            color={processor.isPublished ? 'success' : 'default'}
                                            sx={{
                                                '& .MuiChip-label': {
                                                    color: theme.palette.getContrastText(
                                                        processor.isPublished 
                                                            ? theme.palette.success.main 
                                                            : theme.palette.grey[500]
                                                    ),
                                                },
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        color: theme.palette.text.primary,
                        bgcolor: theme.palette.background.paper,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        '& .MuiTablePagination-select': {
                            color: theme.palette.text.primary,
                        },
                        '& .MuiTablePagination-selectIcon': {
                            color: theme.palette.text.primary,
                        },
                        '& .MuiTablePagination-displayedRows': {
                            color: theme.palette.text.primary,
                        },
                        '& .MuiIconButton-root': {
                            color: theme.palette.text.primary,
                            '&.Mui-disabled': {
                                color: theme.palette.action.disabled,
                            },
                        },
                    }}
                />
            </TableContainer>

            <AddProcessorPanel 
                open={isAddPanelOpen}
                onClose={() => setIsAddPanelOpen(false)}
                onSave={loadProcessors}
            />

            <EditProcessorPanel
                open={isEditPanelOpen}
                onClose={() => setIsEditPanelOpen(false)}
                processor={selectedProcessor}
                onSave={loadProcessors}
            />

            {/* Filter Modal */}
            <Dialog
                open={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                    },
                }}
            >
                <DialogTitle>Filters</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: theme.palette.text.secondary }}>Type</InputLabel>
                            <Select
                                value={filters.type}
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                                label="Type"
                                sx={{
                                    color: theme.palette.text.primary,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.divider,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                            >
                                <MenuItem value="ALL" sx={{ color: theme.palette.text.primary }}>All Types</MenuItem>
                                {PROCESSOR_TYPES.map((type) => (
                                    <MenuItem key={type} value={type} sx={{ color: theme.palette.text.primary }}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <RadioGroup
                                value={filters.isPublished}
                                onChange={(e) => handleFilterChange('isPublished', e.target.value)}
                            >
                                <FormControlLabel 
                                    value="PUBLISHED" 
                                    control={<Radio />} 
                                    label="Published"
                                    sx={{ color: theme.palette.text.primary }}
                                />
                                <FormControlLabel 
                                    value="DRAFT" 
                                    control={<Radio />} 
                                    label="Draft"
                                    sx={{ color: theme.palette.text.primary }}
                                />
                                <FormControlLabel 
                                    value="ALL" 
                                    control={<Radio />} 
                                    label="Both"
                                    sx={{ color: theme.palette.text.primary }}
                                />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setIsFilterModalOpen(false)}
                        sx={{ color: theme.palette.text.primary }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleApplyFilters}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Processors; 